import React, { useCallback, useEffect, useState } from "react";
import { message, Modal } from "antd";
import styled from "styled-components";
import { ArrowLeft } from "lucide-react";
import ViewGerenciarSolicitacoesReserva from "../views/ViewGerenciarSolicitacoesReservas";
import {
  buscarSolicitacaoReservaByIdService,
  buscarHospedesSolicitacaoReservaPorTokenService,
  listarSolicitacoesReservaService,
  alterarStatusSolicitacaoService,
  estornarPagamentoTaxaSolicitacaoPorTokenService,
  buscarAndamentosSolicitacaoService,
  testarEndpointStatus,
  type BuscarSolicitacaoReservaByIdResponse,
  type HospedeSolicitacaoReserva,
  type SolicitacaoReservaListItem,
  type StatusSolicitacaoReserva,
  type AndamentoSolicitacao,
} from "../services/solicitacaoReservaService";
import ViewAppBar from "../views/ViewAppBar";
import ViewFooter from "../views/ViewFooter";
import { usePermissao } from "../uteis/permissaoAdm/usePermissao";

const STATUS_LABELS: Record<StatusSolicitacaoReserva, string> = {
  CRIADA: "Criada",
  SR_ENVIADA: "SR enviada",
  INICIADA: "Iniciada",
  AGUARDANDO_PAGAMENTO_TAXA: "Aguardando taxa",
  PAGAMENTO_TX_INTERROMPIDO: "Pagamento de Taxa Interrompido",
  TAXA_PAGA: "Taxa paga",
  AGUARDANDO_ESTORNO: "Aguardando estorno",
  ESTORNADO: "Estornado",
  FINALIZADO_FRACASSO: "Finalizado fracasso",
  NAO_TEM_MAIS_INTERESSE: "Nao tem mais interesse",
  SEM_INTERESSE_PERIODO_PROCURADO: "Sem interesse no periodo procurado",
  SEM_INTERESSE_PERIODO_ENCONTRADO: "Sem interesse no periodo encontrado",
  EM_VERIFICACAO: "Em verificacao",
  RESERVA_CONFIRMADA: "Reserva confirmada",
  RESERVA_FINALIZADA: "Reserva finalizada",
  RESERVA_CANCELADA: "Reserva cancelada",
};

const STATUS_COM_ALERTA_VINCULOS = new Set<StatusSolicitacaoReserva>([
  "SEM_INTERESSE_PERIODO_PROCURADO",
  "SEM_INTERESSE_PERIODO_ENCONTRADO",
]);

const ContainerComponente = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f0f4f7;

  @media (max-width: 768px) {
    padding: 10px;
  }

  @media (max-width: 480px) {
    padding: 5px;
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 110px;
  left: 40px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  padding: 0.6rem;
  cursor: pointer;
  color: #fff;
  transition: background 0.3s ease, transform 0.2s ease;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  }

  svg {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 768px) {
    top: 90px;
    left: 20px;
    padding: 0.5rem;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

type FiltrosSolicitacaoReserva = {
  contato?: string;
  empreendimento?: string;
  status?: StatusSolicitacaoReserva;
  numeroProtocolo?: string;
};

export default function GerenciarSolicitacoesReservaScreen() {
  const [loading, setLoading] = useState(false);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isLoading, isValid } = usePermissao("OPE");

  const [data, setData] = useState<SolicitacaoReservaListItem[]>([]);
  const [selected, setSelected] =
    useState<BuscarSolicitacaoReservaByIdResponse | null>(null);
  const [filtros, setFiltros] = useState<FiltrosSolicitacaoReserva>({});
  const [novoStatus, setNovoStatus] =
    useState<StatusSolicitacaoReserva | null>(null);
  const [andamentos, setAndamentos] = useState<AndamentoSolicitacao[]>([]);
  const [andamentosDrawerOpen, setAndamentosDrawerOpen] = useState(false);
  const [loadingAndamentos, setLoadingAndamentos] = useState(false);
  const [hospedes, setHospedes] = useState<HospedeSolicitacaoReserva[]>([]);
  const [hospedesModalOpen, setHospedesModalOpen] = useState(false);
  const [loadingHospedes, setLoadingHospedes] = useState(false);
  const [loadingEstornoTaxa, setLoadingEstornoTaxa] = useState(false);

  const carregarLista = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await listarSolicitacoesReservaService(filtros);
      setData(resp);
    } catch (error) {
      console.error(error);
      message.error("Erro ao carregar solicitações.");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  const carregarDetalhe = useCallback(async (id: number) => {
    try {
      setLoadingDetalhe(true);
      const resp = await buscarSolicitacaoReservaByIdService(id);

      if (!resp) {
        message.error("Não foi possível carregar os detalhes da solicitação.");
        return;
      }

      setSelected(resp);
      setDrawerOpen(true);
    } catch (error) {
      console.error(error);
      message.error("Erro ao carregar detalhes.");
    } finally {
      setLoadingDetalhe(false);
    }
  }, []);

  const handleLimpar = () => {
    setFiltros({});
  };

  const handleVoltar = () => {
    window.history.back();
  };

  const handlePesquisar = () => {
    carregarLista();
  };

  const handleOpenDetalhe = (item: SolicitacaoReservaListItem) => {
    carregarDetalhe(item.id);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
    setHospedesModalOpen(false);
    setHospedes([]);
  };

  const handleVerHospedes = useCallback(async (token?: string | null) => {
    if (!token) {
      message.error("Token da solicitação não encontrado.");
      return;
    }

    try {
      setHospedesModalOpen(true);
      setLoadingHospedes(true);
      const resp = await buscarHospedesSolicitacaoReservaPorTokenService(token);
      setHospedes(resp);
    } catch (error) {
      console.error(error);
      message.error("Erro ao carregar hóspedes da solicitação.");
      setHospedes([]);
    } finally {
      setLoadingHospedes(false);
    }
  }, []);

  const handleAlterarStatus = useCallback(
    async (id: number, status: StatusSolicitacaoReserva) => {
      const statusLabel = STATUS_LABELS[status] ?? status;
      const requerAlertaVinculos = STATUS_COM_ALERTA_VINCULOS.has(status);

      Modal.confirm({
        title: "Confirmar alteração de status",
        content: requerAlertaVinculos ? (
          <div>
            <p>
              Tem certeza que deseja alterar o status para{" "}
              <strong>{statusLabel}</strong>?
            </p>
            <p>Essa ação inativará postagens ou interesses que tiverem vinculados.</p>
          </div>
        ) : (
          `Tem certeza que deseja alterar o status para "${statusLabel}"?`
        ),
        okText: "Sim",
        cancelText: "Não",
        onOk: async () => {
          try {
            const sucesso = await alterarStatusSolicitacaoService(id, status);
            if (sucesso) {
              message.success("Status alterado com sucesso!");
              setNovoStatus(null);
              await carregarLista();
              if (selected && selected.id === id) {
                await carregarDetalhe(id);
              }
            } else {
              message.error(
                "Erro ao alterar status. Verifique os logs para mais detalhes."
              );
            }
          } catch (error) {
            console.error(error);
            message.error("Erro ao alterar status.");
          }
        },
      });
    },
    [carregarDetalhe, carregarLista, selected]
  );

  const carregarAndamentos = useCallback(async (id: number) => {
    try {
      setLoadingAndamentos(true);
      const resp = await buscarAndamentosSolicitacaoService(id);
      setAndamentos(resp);
    } catch (error) {
      console.error(error);
      message.error("Erro ao carregar andamentos.");
    } finally {
      setLoadingAndamentos(false);
    }
  }, []);

  const handleVerAndamentos = (id: number) => {
    if (!id || id <= 0) {
      message.error("ID da solicitação inválido");
      return;
    }

    carregarAndamentos(id);
    setAndamentosDrawerOpen(true);
  };

  const handleTestarEndpoint = useCallback(async (id: number) => {
    const resultado = await testarEndpointStatus(id);
    if (resultado.sucesso) {
      message.success(resultado.mensagem);
    } else {
      message.error(`Problema detectado: ${resultado.mensagem}`);
    }
  }, []);

  const handleEstornarTaxa = useCallback(() => {
    if (!selected?.token) {
      message.error("Token da solicitação não encontrado.");
      return;
    }

    Modal.confirm({
      title: "Confirmar estorno da taxa",
      content: "Tem certeza que deseja estornar a taxa desta solicitação?",
      okText: "Sim",
      cancelText: "Não",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setLoadingEstornoTaxa(true);
          const sucesso = await estornarPagamentoTaxaSolicitacaoPorTokenService(
            selected.token
          );

          if (!sucesso) {
            message.error("Erro ao estornar a taxa.");
            return;
          }

          message.success("Solicitação de estorno enviada com sucesso!");
          await carregarLista();
          await carregarDetalhe(selected.id);
        } catch (error) {
          console.error(error);
          message.error("Erro ao estornar a taxa.");
        } finally {
          setLoadingEstornoTaxa(false);
        }
      },
    });
  }, [carregarDetalhe, carregarLista, selected]);

  if (isLoading) return <p>Verificando permissão...</p>;
  if (!isValid) return null;

  return (
    <ContainerComponente>
      <BackButton onClick={handleVoltar} title="Voltar">
        <ArrowLeft />
      </BackButton>

      <ViewAppBar />
      <ViewGerenciarSolicitacoesReserva
        loading={loading}
        loadingDetalhe={loadingDetalhe}
        data={data}
        selected={selected}
        drawerOpen={drawerOpen}
        filtros={filtros}
        onChangeFiltros={setFiltros}
        onPesquisar={handlePesquisar}
        onLimpar={handleLimpar}
        onOpenDetalhe={handleOpenDetalhe}
        onCloseDrawer={handleCloseDrawer}
        novoStatus={novoStatus}
        onChangeNovoStatus={setNovoStatus}
        onAlterarStatus={handleAlterarStatus}
        hospedes={hospedes}
        hospedesModalOpen={hospedesModalOpen}
        loadingHospedes={loadingHospedes}
        onVerHospedes={handleVerHospedes}
        onCloseHospedesModal={() => {
          setHospedesModalOpen(false);
          setHospedes([]);
        }}
        andamentos={andamentos}
        andamentosDrawerOpen={andamentosDrawerOpen}
        loadingAndamentos={loadingAndamentos}
        onVerAndamentos={handleVerAndamentos}
        onCloseAndamentosDrawer={() => setAndamentosDrawerOpen(false)}
        loadingEstornoTaxa={loadingEstornoTaxa}
        onEstornarTaxa={handleEstornarTaxa}
        onTestarEndpoint={handleTestarEndpoint}
      />
      <ViewFooter />
    </ContainerComponente>
  );
}
